import React, { useState, useEffect, useCallback } from 'react';
import { Col, Container, Row, Input, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Button, Label } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { Edit, Info as InfoIcon, UserPlus, UserX, User } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { fetchTableData, fetchFormData, assignToSelf, releaseFromSelf, fetchUsersByRole, assignTaskToUser, resetTaskAssignment } from '../../../../Api/ApiMethods';
import PatchCallFormWorkInfoModal from '../PatchCallFormWorkInfoModal';
import { toast } from 'react-toastify';
import { actionButtonColumn } from '../../../Common/TableColumns';
import { getTodoRoleStateFilters } from '../../../../Config/Config';

const TodoTabAllTasks = ({ onCountChange }) => {
    const activeTabId = '1';
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [callFormStates, setCallFormStates] = useState({});
    const [callFormStatesList, setCallFormStatesList] = useState([]);
    const [selectedStateFilter, setSelectedStateFilter] = useState('');
    const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
    const navigate = useNavigate();

    const [isPatchModalOpen, setIsPatchModalOpen] = useState(false);
    const [currentPatchCallFormId, setCurrentPatchCallFormId] = useState(null);
    const [currentPatchInitialData, setCurrentPatchInitialData] = useState(null);

    const [isAssigningTask, setIsAssigningTask] = useState(false);
    const [isReleasingTask, setIsReleasingTask] = useState(false);
    const [isRemovingAssignment, setIsRemovingAssignment] = useState(false);

    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [jobToAssignId, setJobToAssignId] = useState(null);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);

    const callFormApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callform';
    const callFormStateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callformstate';

    useEffect(() => {
        setSelectedStateFilter('');
        setPage(1);
    }, [activeTabId]);

    useEffect(() => {
        const handleRoleChange = () => {
            const newUserRole = localStorage.getItem('userRole');
            setUserRole(newUserRole);

            setSelectedStateFilter('');
            setPage(1);
        };

        window.addEventListener('userRoleChanged', handleRoleChange);

        return () => {
            window.removeEventListener('userRoleChanged', handleRoleChange);
        };
    }, []);

    useEffect(() => {
        const fetchStates = async () => {
            try {
                // Backend'e isInactive=false ile sorgula — inactive state'ler hariç tutulur
                const response = await fetchFormData({
                    apiEndpoint: `${callFormStateApiUrl}?isInactive=false`,
                    page: 1,
                    pageSize: 100
                });

                const allItems = Array.isArray(response) ? response : [];

                if (allItems.length > 0) {
                    const stateMap = {};
                    allItems.forEach(item => {
                        if (item && item.id && item.title) {
                            stateMap[item.id] = item.title;
                        }
                    });
                    setCallFormStates(stateMap);

                    const allowedTitles = getTodoRoleStateFilters(userRole);
                    const showAllStates = !allowedTitles || userRole === 'COORDINATOR' || userRole === 'ADMIN';

                    const validItems = allItems.filter(item => item && item.id && item.title);

                    const filteredItems = validItems.filter(item => {
                        const isAllowed = showAllStates || (allowedTitles && allowedTitles.includes(item.title));
                        return isAllowed;
                    });

                    const statesForDropdown = filteredItems.map(item => ({ id: item.id, title: item.title }));

                    setCallFormStatesList(statesForDropdown);

                } else {
                    setCallFormStates({});
                    setCallFormStatesList([]);
                }
            } catch (error) {
                toast.error('Durum listesi alınırken bir hata oluştu.');
                setCallFormStates({});
                setCallFormStatesList([]);
            }
        };

        if (userRole) {
            fetchStates();
        }
    }, [callFormStateApiUrl, userRole, activeTabId]);


    const fetchData = useCallback(async () => {


        setLoading(true);
        let apiEndpoint = callFormApiUrl;
        const queryParams = new URLSearchParams();

        if (selectedStateFilter && selectedStateFilter !== 'all' && selectedStateFilter !== '') {
            queryParams.append('callformstate', selectedStateFilter);
        } else {
            // Tümü seçili — inactive state'leri hariç tutmak için aktif state ID'lerini virgüllü gönder
            const activeIds = callFormStatesList.map(s => s.id).filter(Boolean);
            if (activeIds.length > 0) {
                queryParams.append('callFormStateIds', activeIds.join(','));
            }
        }

        if (search) {
            queryParams.append('search', search);
        }

        const queryString = queryParams.toString();
        if (queryString) {
            apiEndpoint += `?${queryString}`;
        }

        try {
            await fetchTableData({
                apiEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setData,
                setTotalRowsCallback: (newTotalRows) => {
                    setTotalRows(newTotalRows);
                    if (onCountChange) {
                        onCountChange(newTotalRows);
                    }
                },
            });
        } catch (error) {
            toast.error('Veriler alınırken bir hata oluştu.');
            setData([]);
            setTotalRows(0);
            if (onCountChange) {
                onCountChange(0);
            }
        } finally {
            setLoading(false);
        }
    }, [callFormApiUrl, page, pageSize, search, selectedStateFilter, onCountChange, activeTabId, callFormStatesList]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
        setPage(1);
    };

    const handleStateFilterChange = (event) => {
        setSelectedStateFilter(event.target.value);
        setPage(1);
    };

    const handleEdit = (id) => {
        navigate(`/callform/${id}`);
    };

    const openPatchModal = (row) => {
        if (row && row.id) {
            const accidentData = row.accidents && row.accidents.length > 0 ? row.accidents[0] : {};

            setCurrentPatchCallFormId(row.id);
            setCurrentPatchInitialData({
                isTrialDocument: accidentData.isTrialDocument,
                bonusEligibility: accidentData.bonusEligibility,
            });
            setIsPatchModalOpen(true);
        } else {
            toast.error('İşlem için geçerli kayıt bulunamadı.');
        }
    };

    const togglePatchModal = () => {
        setIsPatchModalOpen(!isPatchModalOpen);
        if (isPatchModalOpen) {
            setCurrentPatchCallFormId(null);
            setCurrentPatchInitialData(null);
        }
    };

    const fetchEmployees = async (userRoleFilter) => {
        try {
            const users = await fetchUsersByRole({ userRole: userRoleFilter });

            const formattedUsers = users
                .filter(user => user.id && user.firstName && user.lastName)
                .map(user => ({
                    id: user.id,
                    name: `${user.firstName} ${user.lastName}`
                }));

            setEmployeeList(formattedUsers);
        } catch (error) {
            setEmployeeList([]);
        }
    };

    const toggleAssignModal = (jobId = null) => {
        if (jobId) {
            setSelectedUserId('');
            setJobToAssignId(jobId);

            const jobRecord = data.find(item => item.id === jobId);
            if (jobRecord) {
                const stateObject = Array.isArray(jobRecord.callFormState) ? jobRecord.callFormState[0] : jobRecord.callFormState;
                const stateTitle = stateObject?.title || '';

                let userRoleFilter = 'appraiser';
                if (stateTitle === 'Eksper Onay Bekleniyor') {
                    userRoleFilter = 'appraiser';
                } else if (stateTitle === 'Evrak Bekleniyor') {
                    userRoleFilter = 'documentrequest';
                }

                fetchEmployees(userRoleFilter);
            }
        } else {
            setJobToAssignId(null);
        }
        setAssignModalOpen(!assignModalOpen);
    };

    const handleAssignUser = async () => {
        if (!jobToAssignId) {
            toast.error('Atanacak iş ID bulunamadı.');
            return;
        }
        if (!selectedUserId) {
            toast.error('Lütfen atanacak bir personel seçin.');
            return;
        }

        setIsAssigning(true);

        try {
            const responseData = await assignTaskToUser({
                resource: 'callform',
                id: jobToAssignId,
                userId: selectedUserId
            });

            if (responseData && responseData.isSuccess !== false) {
                toast.success('İş başarıyla personele atandı!');
                fetchData();
                toggleAssignModal();
            } else {
                const errorMessage = responseData?.errors?.userId?.[0] || responseData?.message || 'İş atanırken bir hata oluştu.';
                toast.error(errorMessage);
            }
        } catch (error) {
            toast.error('İş atanırken bir hata oluştu.');
        } finally {
            setIsAssigning(false);
        }
    };

    const handleRemoveAssignment = async (callFormId) => {
        if (isRemovingAssignment) return;

        setIsRemovingAssignment(true);
        try {
            const responseData = await resetTaskAssignment({
                resource: 'callform',
                id: callFormId
            });

            if (responseData && responseData.isSuccess !== false) {
                toast.success(responseData.message || 'İş ataması başarıyla kaldırıldı!');
                fetchData();
            } else {
                toast.error(responseData?.message || 'İş ataması kaldırılırken bir hata oluştu.');
            }
        } catch (error) {
            toast.error('İş ataması kaldırılırken bir hata oluştu.');
        } finally {
            setIsRemovingAssignment(false);
        }
    };

    const handleAssignToSelf = async (callFormId) => {
        if (isAssigningTask) return;

        setIsAssigningTask(true);
        try {
            const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/callForm/assign-task-to-user`;

            const responseData = await assignToSelf({
                apiEndpoint,
                callFormId
            });

            toast.success(responseData?.message || 'İş başarıyla kendinize atandı!');
            fetchData();

        } catch (error) {
            if (!error.response || error.response.status >= 500) {
                toast.error('İş kendinize atanırken bir hata oluştu.');
            }
        } finally {
            setIsAssigningTask(false);
        }
    };

    const handleReleaseFromSelf = async (callFormId) => {
        if (isReleasingTask) return;

        setIsReleasingTask(true);
        try {
            const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/callForm/release-task-from-user`;

            const responseData = await releaseFromSelf({
                apiEndpoint,
                callFormId
            });

            toast.success(responseData?.message || 'İş başarıyla kendinizden kaldırıldı!');
            fetchData();

        } catch (error) {
            if (!error.response || error.response.status >= 500) {
                toast.error('İş kendinizden kaldırılırken bir hata oluştu.');
            }
        } finally {
            setIsReleasingTask(false);
        }
    };

    const columns = [
        {
            name: 'Çağrı Formu No',
            selector: row => row.callFormNumber || '-',
            sortable: true,
        },
        {
            name: 'Müşteri',
            selector: row => {
                const customer = row.customers?.[0];
                return customer?.clientName || customer?.companyName || '-';
            },
            sortable: true,
            grow: 2
        },
        {
            name: 'Plaka',
            selector: row => row.licensePlates?.[0]?.licensePlateNumber || '-',
            sortable: true
        },
        {
            name: 'Vekaletname No',
            selector: row => row.powerOfAttorneys?.[0]?.poaNumber || '-',
            sortable: true
        },
        {
            name: 'Kaza Dosya No',
            selector: row => row.accidentFiles?.[0]?.fileNumber || '-',
            sortable: true
        },
        {
            name: 'Durum',
            selector: row => {
                const stateObject = Array.isArray(row.callFormState) ? row.callFormState[0] : row.callFormState;
                const stateIdValue = (typeof stateObject === 'object' && stateObject !== null) ? stateObject.id : stateObject;

                if (stateIdValue) {
                    return callFormStates[stateIdValue] || stateIdValue;
                }
                return '-';
            },
            sortable: true,
            grow: 1.5
        },
        actionButtonColumn({
            name: 'Düzenle',
            icon: Edit,
            onClick: (row) => handleEdit(row.id)
        }),
        {
            name: 'Kendine Ata/Kaldır',
            cell: (row) => {
                const isRowLocked = row?.isLocked === true;

                if (isRowLocked) {
                    return (
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            title="Kilitli"
                            disabled
                        >
                            <User size={18} />
                        </button>
                    );
                }

                if (!row.appointedUser) {
                    return (
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => handleAssignToSelf(row.id)}
                            title="Kendine Ata"
                            disabled={isAssigningTask}
                        >
                            <UserPlus size={18} />
                        </button>
                    );
                }

                if (row.canRelease) {
                    const stateObject = Array.isArray(row.callFormState) ? row.callFormState[0] : row.callFormState;
                    const stateTitle = stateObject?.title || '';

                    if (userRole === 'APPRAISER' && stateTitle === 'Eksper Onay Bekleniyor') {
                        return null;
                    }

                    if (userRole === 'DOCUMENT REQUEST' && stateTitle === 'Evrak Bekleniyor') {
                        return null;
                    }

                    return (
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleReleaseFromSelf(row.id)}
                            title="Kendinden Kaldır"
                            disabled={isReleasingTask}
                        >
                            <UserX size={18} />
                        </button>
                    );
                }

                return null;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            right: true,
        },
    ];

    if (userRole === 'COORDINATOR' || userRole === 'MANAGEMENT' || userRole === 'HEAD APPRAISER') {
        columns.push(
            {
                name: 'İş Ata/Kaldır',
                cell: (row) => {
                    const isRowLocked = row?.isLocked === true;
                    const stateObject = Array.isArray(row.callFormState) ? row.callFormState[0] : row.callFormState;
                    const stateTitle = stateObject?.title || '';

                    const isEksperOnayBekleniyor = stateTitle === 'Eksper Onay Bekleniyor';

                    const isEvrakBekleniyor = stateTitle === 'Evrak Bekleniyor' &&
                        (userRole === 'COORDINATOR' || userRole === 'MANAGEMENT');

                    if (!isEksperOnayBekleniyor && !isEvrakBekleniyor) return null;

                    if (isRowLocked) {
                        return (
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                title="Kilitli"
                                disabled
                            >
                                <User size={18} />
                            </button>
                        );
                    }

                    if (!row.appointedUser) {
                        return (
                            <button
                                type="button"
                                className="btn btn-outline-success"
                                onClick={() => toggleAssignModal(row.id)}
                                disabled={isAssigning}
                                title="İş Ata"
                            >
                                <UserPlus size={18} />
                            </button>
                        );
                    }

                    return (
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleRemoveAssignment(row.id)}
                            disabled={isRemovingAssignment}
                            title="İş Atamasını Kaldır"
                        >
                            <UserX size={18} />
                        </button>
                    );
                },
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            }
        );
    }

    if (userRole === 'COORDINATOR' || userRole === 'MANAGEMENT') {
        columns.push({
            name: 'Bilgi Güncelle',
            cell: (row) => {
                const isRowLocked = row?.isLocked === true;
                return (
                    <button
                        type="button"
                        className="btn btn-outline-info"
                        onClick={() => !isRowLocked && openPatchModal(row)}
                        title="Dosya Bilgisi"
                        disabled={isRowLocked}
                    >
                        <InfoIcon size={18} />
                    </button>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        });
    }

    useEffect(() => {
        setSelectedStateFilter('');
        setSearch('');
        setPage(1);
    }, [activeTabId]);

    return (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="Çağrı formu no, müşteri adı vb. ile ara..."
                                    value={search}
                                    onChange={handleSearchChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>Durum Filtrele</Label>
                                <Input
                                    type="select"
                                    value={selectedStateFilter}
                                    onChange={handleStateFilterChange}
                                >
                                    {activeTabId !== '6' && (
                                        <option value="">Tümü</option>
                                    )}
                                    {callFormStatesList.map(state => (
                                        <option key={state.id} value={state.id}>
                                            {state.title}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <DataTable
                        columns={columns}
                        data={data}
                        pagination
                        paginationServer
                        stickyActionPosition="right"
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={(newPageSize) => {
                            setPageSize(newPageSize);
                            setPage(1);
                        }}
                        onChangePage={setPage}
                        progressPending={loading}
                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                        responsive
                        noDataComponent={loading ? null : 'Veri bulunamadı'}
                    />

                    <Modal isOpen={assignModalOpen} toggle={() => toggleAssignModal()}>
                        <ModalHeader toggle={() => toggleAssignModal()}>
                            Personel Ata
                        </ModalHeader>
                        <ModalBody>
                            <FormGroup>
                                <Label for="userSelect">Personel Seçin</Label>
                                <Input
                                    type="select"
                                    id="userSelect"
                                    value={selectedUserId}
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                >
                                    <option value="">Personel seçin...</option>
                                    {employeeList.map(employee => (
                                        <option key={employee.id} value={employee.id}>
                                            {employee.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => toggleAssignModal()}>
                                İptal
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleAssignUser}
                                disabled={isAssigning || !selectedUserId}
                            >
                                {isAssigning ? 'Atanıyor...' : 'Ata'}
                            </Button>
                        </ModalFooter>
                    </Modal>

                    <PatchCallFormWorkInfoModal
                        isOpen={isPatchModalOpen}
                        toggle={togglePatchModal}
                        callFormId={currentPatchCallFormId}
                        initialData={currentPatchInitialData}
                        onSuccess={() => {
                            fetchData();
                            togglePatchModal();
                        }}
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default TodoTabAllTasks;

